import { memo, useEffect, useState } from 'react';
import { Chart, ChartType, TooltipModel } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import { NoData } from '../../widgets/WidgetStates';

import { getBarThickness } from '../../../utilities/common/getBarThikness';
import { useDeviceType } from '../../../utilities/common/deviceType';
import { tooltipHandler } from '../../../utilities/common/chart';

import '../Layout/index.css';

interface IBarChartProps {
  id?: any;
  legend?: boolean;
  data?: any;

  className?: string;
  labels?: boolean;
  backgroundColors: string[];
}

function BarChartVertical({
  id,
  data,
  legend,
  className,
  backgroundColors,
}: IBarChartProps) {
  Chart.register(ChartDataLabels);

  const [legendHTML, setLegendHTML] = useState('');
  const isMobile = useDeviceType();

  useEffect(() => {
    const generateLegend = (chartData: any) => {
      const chart = Chart.getChart(id);
      if (chart) {
        const text = [];
        text.push('<ul class="' + chart.id + '-legend">');

        for (let i = 0; i < chartData.datasets.length; i++) {
          text.push(
            `<li><span class="legend-container-color intl-widget-ignore-element" style="background-color:${
              chartData.datasets[i].legendColor
            }"></span><span class="capitalize">${chartData.datasets[
              i
            ].label?.toLowerCase()}</span></li>`,
          );
        }
        text.push('</ul>');
        return text.join('');
      }
      return '';
    };

    // Update legendHTML on data change
    setLegendHTML(generateLegend(data)); // Update state with generated HTML
  }, [data]);

  const options: any = {
    indexAxis: 'x',
    responsive: true,
    maintainAspectRatio: false,
    backgroundColor: backgroundColors,
    borderRadius: '4',
    barThickness: getBarThickness(data?.labels.length, isMobile),
    // barPercentage: 0.5,
    // categoryPercentage: 0.9,
    centerBars: true,
    plugins: {
      title: {
        display: false,
      },
      datalabels: {
        // display: true,
        // display: !!labels,
        anchor: 'end',
        align: 'end',
        font: {
          size: '10px',
          family: "'Inter', sans-serif",
          weight: 500,
        },
        color: '#475467',
        // backgroundColor: '#EAECF0',
        // borderRadius: 3,
        // padding: {
        //   top: 3,
        //   bottom: 3,
        //   left: 3,
        //   right: 3,
        // },
        padding: 0,
        formatter: (value: any, context: any) => {
          const datasetArray: any = [];
          // console.log(
          //   'context.chart.data.dataset',
          //   context.chart.data.datasets,
          // );
          context.chart.data.datasets.forEach((dataset: any) => {
            if (dataset.data[context.dataIndex] != undefined) {
              datasetArray.push(dataset.data[context.dataIndex]);
            }
          });

          function totalSum(total: any, dataPoint: any) {
            // console.log('total', typeof total);
            return Number(total) + Number(dataPoint);
          }

          let sum = datasetArray.reduce(totalSum, 0);

          if (context.datasetIndex === datasetArray.length - 1) {
            return sum > 0 ? sum : '';
          } else {
            return '';
          }
        },
      },

      legend: {
        display: false,
      },

      tooltip: {
        enabled: false,
        external: (ctx: { tooltip: TooltipModel<ChartType> }) => {
          const { tooltip } = ctx;

          // Build title HTML string safely
          const titleHTML = (tooltip.title || [])
            .map((titleText) => `<span>${titleText}</span>`)
            .join('');

          // Join body text lines
          const bodyText = tooltip.body?.map((b) => b.lines).join('\n') || '';

          // Create content div
          const content = document.createElement('div');
          content.classList.add('chart-tooltip-vertical');
          content.innerHTML = `
						${titleHTML}
						<p><span class="colorSquare"></span>${bodyText}</p>
					`;

          tooltipHandler(ctx, content);
        },
        // yAlign: 'bottom',
        // backgroundColor: '#fff',
        // borderWidth: 1,
        // borderColor: '#EAECF0',
        // titleColor: '#667085',
        // bodyColor: '#000',
        // displayColors: false,

        // callbacks: {
        //   //   title: (tooltipItems: any) => {
        //   //     return tooltipItems[0].parsed.formattedValue;
        //   // 	},

        //   external: function (context) {
        //     return 'DSf';
        //   },
        // },
      },
    },
    layout: {
      padding: {
        left: -20,
      },
    },
    animation: false,
    scales: {
      x: {
        grid: { display: false, drawTicks: false, color: '#F2F4F7' },
        // display: false,
        stacked: true,
      },
      y: {
        stacked: true,
        border: {
          display: false,
        },

        ticks: {
          padding: 35,
          font: {
            family: "'Inter', sans-serif",
            size: 12,
          },
          // stepSize: function (value, index, values) {
          //   // Get the range of the dataset
          //   var range = value.scale.max / value.scale._ticksLength;
          //   // Set the step size to double the range
          //   console.log(
          //     'my ranges',
          //     value.scale.max / value.scale._ticksLength,
          //   );
          //   return range;
          // },
          color: '#475467',
        },
      },
    },
  };

  // const subbox = document.querySelector('.subbox');
  // if (subbox) {
  //   subbox.style.height = '322px';
  // }

  // useEffect(() => {
  //   return () => {
  //     const existingChart = Chart.getChart(id);
  //     if (existingChart) {
  //       existingChart.destroy();
  //     }
  //   };
  // }, []);

  return data == null ? (
    <NoData />
  ) : (
    // adding a negative top margin to align the chart properly within its container
    <div
      className={`intl-bar-chart-scrollable relative -mt-6 min-h-[350px] w-full overflow-auto md:pr-7 ${className}`}
    >
      <div
        className={`subbox pr-10 lg:pr-0 ${
          data?.labels.length > 100
            ? 'min-w-[1200px]'
            : data?.labels.length > 84
              ? 'min-w-[1000px]'
              : data?.labels.length > 72
                ? 'min-w-[800px]'
                : data?.labels.length > 60
                  ? 'min-w-[600px]'
                  : 'min-w-[600px]'
        }`}
      >
        <Bar data={data} options={options} className="" id={id} />
      </div>
      {!!legend && (
        <div
          className="legend-container text-grey-800 sticky top-0 z-10 mb-6 flex h-[60px] w-full justify-center gap-x-4 bg-white text-sm"
          id={`legend-container` + id}
          dangerouslySetInnerHTML={{ __html: legendHTML }}
        />
      )}
    </div>
  );
}

export default memo(BarChartVertical);
